import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaGlobeAmericas, FaLightbulb, FaChalkboardTeacher, FaUsers, FaCode, FaTimes } from 'react-icons/fa';
import './Exploration.css';

interface ExplorationCard {
  id: string;
  title: string;
  description: string;
  category: string;
  image: string;
  icon: JSX.Element;
  year: string;
}

const explorationData: ExplorationCard[] = [
  {
    id: "tech-frontiers",
    title: "Tech Frontiers",
    description: "Leading innovation in ML/AI and data engineering. Pushing boundaries in technological advancement and research.",
    category: "Technical Innovation",
    image: "https://picsum.photos/seed/techfront/800/450",
    icon: <FaLightbulb />,
    year: "2023"
  },
  {
    id: "global-impact",
    title: "Global Impact",
    description: "Creating solutions that transcend borders. Building systems that make a difference worldwide.",
    category: "Global Reach",
    image: "https://picsum.photos/seed/globalimpact/800/450",
    icon: <FaGlobeAmericas />,
    year: "2022"
  },
  {
    id: "knowledge-sharing",
    title: "Knowledge Frontiers",
    description: "Sharing expertise through mentorship and leadership. Guiding the next generation of innovators.",
    category: "Leadership",
    image: "https://picsum.photos/seed/knowledge/800/450",
    icon: <FaChalkboardTeacher />,
    year: "2022"
  },
  {
    id: "community-building",
    title: "Community Catalyst",
    description: "Building and nurturing tech communities. Creating spaces for collaboration and growth.",
    category: "Community",
    image: "https://picsum.photos/seed/community/800/450",
    icon: <FaUsers />,
    year: "2021"
  },
  {
    id: "code-adventures",
    title: "Code Expeditions",
    description: "Venturing into new programming territories. Mastering diverse technologies and frameworks.",
    category: "Technical Skills",
    image: "https://picsum.photos/seed/codeexp/800/450",
    icon: <FaCode />,
    year: "2021"
  }
];

const Exploration: React.FC = () => {
  const [selectedCard, setSelectedCard] = useState<ExplorationCard | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Modal handlers
  const handleCardClick = (card: ExplorationCard) => {
    console.log("Card clicked:", card);
    setSelectedCard(card);
  };

  const closeModal = () => {
    setSelectedCard(null);
  };

  return (
    <div className="exploration-container">
      <motion.div 
        className="hero-section"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <h1>Explore The Journey</h1>
        <p>Where Technology Meets Adventure</p>
      </motion.div>

      <motion.div 
        className="exploration-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {explorationData.map((card) => (
          <motion.div
            key={card.id}
            className={`exploration-card ${hoveredCard === card.id ? 'hovered' : ''}`}
            variants={cardVariants}
            onClick={() => handleCardClick(card)}
            onHoverStart={() => setHoveredCard(card.id)}
            onHoverEnd={() => setHoveredCard(null)}
          >
            <div className="card-image">
              <img src={card.image} alt={card.title} />
              <div className="card-overlay">
                <div className="card-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <span className="card-category">{card.category}</span>
                <span className="card-year">{card.year}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {selectedCard && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
          key="modal"
        >
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <button className="modal-close" onClick={closeModal}>
              <FaTimes />
            </button>
            <img src={selectedCard.image} alt={selectedCard.title} />
            <div className="modal-info">
              <h2>{selectedCard.title}</h2>
              <p className="modal-category">{selectedCard.category} • {selectedCard.year}</p>
              <p className="modal-description">{selectedCard.description}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Exploration; 